import React, { useState, useEffect, useCallback } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Loader2, FlaskConical, TrendingUp, TrendingDown, Shield, AlertTriangle, Save, Trash2, ArrowRightLeft, Send, SlidersHorizontal, ChevronDown, ChevronUp } from 'lucide-react';
import { apiGet, apiPost, apiDelete } from '../data/api';

/* ─── Helpers ───────────────────────────────────────────────── */
function MetricBadge({ label, value, suffix = '', good }) {
  const color = good === undefined ? 'secondary' : good ? 'default' : 'destructive';
  return (
    <Badge variant={color} className="text-xs font-mono">
      {label}: {typeof value === 'number' ? (value >= 0 ? '+' : '') + value : value}{suffix}
    </Badge>
  );
}

function MetricsRow({ m, compact = false }) {
  if (!m) return <span className="text-zinc-500 text-xs">No data</span>;
  return (
    <div className="flex flex-wrap gap-1.5">
      <MetricBadge label="N" value={m.sample_size} />
      <MetricBadge label="WR" value={m.win_rate} suffix="%" good={m.win_rate > 55} />
      <MetricBadge label="ROI" value={m.roi} suffix="%" good={m.roi > 0} />
      <MetricBadge label="P/L" value={m.profit} suffix="u" good={m.profit > 0} />
      <MetricBadge label="AvgOdds" value={m.avg_odds} />
      <MetricBadge label="AvgEV" value={m.avg_ev} suffix="%" />
      {!compact && <MetricBadge label="Impl" value={m.avg_implied} suffix="%" />}
      {!compact && <MetricBadge label="PF" value={m.profit_factor} good={m.profit_factor > 1.2} />}
      {!compact && <MetricBadge label="DD" value={m.max_drawdown} suffix="u" />}
      {!m.is_reliable && <Badge variant="outline" className="text-xs text-amber-500 border-amber-500">Low sample</Badge>}
    </div>
  );
}

function FilterTags({ filters }) {
  return (
    <div className="flex flex-wrap gap-1">
      {filters.map((f, i) => (
        <Badge key={i} variant="outline" className="text-xs font-mono bg-zinc-800/50">{f}</Badge>
      ))}
    </div>
  );
}

function RobustnessScore({ score }) {
  let color = 'text-zinc-500';
  let label = 'Low';
  if (score > 30) { color = 'text-emerald-400'; label = 'Strong'; }
  else if (score > 15) { color = 'text-blue-400'; label = 'Good'; }
  else if (score > 5) { color = 'text-amber-400'; label = 'Fair'; }
  else if (score > 0) { color = 'text-orange-400'; label = 'Weak'; }
  else { color = 'text-red-400'; label = 'Poor'; }
  return (
    <span className={`text-xs font-mono font-bold ${color}`} title={`Robustness: ${score}`}>
      R:{score} ({label})
    </span>
  );
}

function WarningBadges({ warnings }) {
  if (!warnings || warnings.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-1 mt-1">
      {warnings.map((w, i) => (
        <Badge key={i} variant="outline" className="text-xs text-amber-400 border-amber-600 bg-amber-950/30">
          <AlertTriangle size={10} className="mr-1" />{w.reason}
        </Badge>
      ))}
    </div>
  );
}

function PatternCard({ pattern, rank, onCompareA, onCompareB }) {
  const m = pattern.metrics;
  return (
    <div className="border border-zinc-800 rounded-lg p-3 space-y-2" data-testid={`pattern-card-${rank}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-zinc-500 text-xs font-mono">#{rank}</span>
          <FilterTags filters={pattern.filters} />
          <RobustnessScore score={pattern.rank_score} />
        </div>
        <div className="flex gap-1">
          {onCompareA && (
            <Button variant="ghost" size="sm" className="h-6 px-2 text-xs" onClick={() => onCompareA(pattern.filters)}>A</Button>
          )}
          {onCompareB && (
            <Button variant="ghost" size="sm" className="h-6 px-2 text-xs" onClick={() => onCompareB(pattern.filters)}>B</Button>
          )}
        </div>
      </div>
      <MetricsRow m={m} />
      <WarningBadges warnings={pattern.warnings} />
    </div>
  );
}

/* ─── Sort/Filter Controls ──────────────────────────────────── */
const SORT_OPTIONS = [
  { value: 'rank_score', label: 'Robustness' },
  { value: 'roi', label: 'ROI' },
  { value: 'sample_size', label: 'Sample Size' },
  { value: 'profit_factor', label: 'Profit Factor' },
  { value: 'max_drawdown', label: 'Max Drawdown' },
  { value: 'profit', label: 'Profit' },
];

const MIN_SAMPLE_OPTIONS = [15, 30, 50, 75, 100];

function sortPatterns(patterns, sortBy, ascending) {
  return [...patterns].sort((a, b) => {
    let va, vb;
    if (sortBy === 'rank_score') { va = a.rank_score; vb = b.rank_score; }
    else if (sortBy === 'max_drawdown') { va = a.metrics.max_drawdown; vb = b.metrics.max_drawdown; }
    else { va = a.metrics[sortBy]; vb = b.metrics[sortBy]; }
    return ascending ? va - vb : vb - va;
  });
}

/* ─── Overview Tab ──────────────────────────────────────────── */
function OverviewTab({ data }) {
  if (!data) return null;
  const markets = [
    { key: 'mw', label: 'Match Winner', icon: '🎯' },
    { key: 'hc', label: 'Handicap', icon: '📊' },
    { key: 'ou', label: 'Over/Under', icon: '📈' },
  ];
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="overview-tab">
      {markets.map(({ key, label, icon }) => {
        const m = data[key]?.metrics;
        return (
          <Card key={key} className="bg-zinc-900/80 border-zinc-800">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <span>{icon}</span> {label}
                <Badge variant="outline" className="ml-auto text-xs">{data[key]?.total_bets} bets</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {m ? (
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>Win Rate: <span className={m.win_rate > 55 ? 'text-emerald-400' : 'text-zinc-400'}>{m.win_rate}%</span></div>
                  <div>ROI: <span className={m.roi > 0 ? 'text-emerald-400' : 'text-red-400'}>{m.roi > 0 ? '+' : ''}{m.roi}%</span></div>
                  <div>Profit: <span className={m.profit > 0 ? 'text-emerald-400' : 'text-red-400'}>{m.profit > 0 ? '+' : ''}{m.profit}u</span></div>
                  <div>PF: <span className={m.profit_factor > 1.2 ? 'text-emerald-400' : 'text-zinc-400'}>{m.profit_factor}</span></div>
                  <div>Max DD: <span className="text-amber-400">{m.max_drawdown}u</span></div>
                  <div>Avg Odds: <span className="text-zinc-400">{m.avg_odds}</span></div>
                  <div>Avg EV: <span className="text-zinc-400">{m.avg_ev}%</span></div>
                  <div>Avg Impl: <span className="text-zinc-400">{m.avg_implied}%</span></div>
                </div>
              ) : <span className="text-zinc-500">No data</span>}
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}

/* ─── Pattern Section with Sort ─────────────────────────────── */
function PatternSection({ title, icon: Icon, patterns, sortBy, ascending, onCompareA, onCompareB, defaultShow = 10 }) {
  const [showAll, setShowAll] = useState(false);
  const sorted = sortPatterns(patterns, sortBy, ascending);
  const visible = showAll ? sorted : sorted.slice(0, defaultShow);

  return (
    <div>
      <h3 className="text-sm font-semibold text-zinc-300 mb-2 flex items-center gap-2">
        <Icon size={14} /> {title} ({patterns.length})
      </h3>
      <div className="space-y-2">
        {visible.map((p, i) => (
          <PatternCard key={i} pattern={p} rank={i + 1} onCompareA={onCompareA} onCompareB={onCompareB} />
        ))}
      </div>
      {patterns.length > defaultShow && (
        <Button variant="ghost" size="sm" className="mt-2 text-xs text-zinc-500" onClick={() => setShowAll(!showAll)}>
          {showAll ? <><ChevronUp size={12} className="mr-1" />Show less</> : <><ChevronDown size={12} className="mr-1" />Show all {patterns.length}</>}
        </Button>
      )}
    </div>
  );
}

/* ─── Market Analyzer Tab ───────────────────────────────────── */
function MarketAnalyzerTab({ market, label }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [minSample, setMinSample] = useState(15);
  const [sortBy, setSortBy] = useState('rank_score');
  const [ascending, setAscending] = useState(false);
  const [compareA, setCompareA] = useState(null);
  const [compareB, setCompareB] = useState(null);
  const [compareResult, setCompareResult] = useState(null);
  const [comparing, setComparing] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const json = await apiGet(`/tt/pattern-lab/analyze/${market}?min_sample=${minSample}`);
      setData(json);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [market, minSample]);

  useEffect(() => { load(); }, [load]);

  const runCompare = async () => {
    if (!compareA || !compareB) return;
    setComparing(true);
    try {
      const result = await apiPost('/tt/pattern-lab/compare', { market, pattern_a: compareA, pattern_b: compareB });
      setCompareResult(result);
    } catch (e) { console.error(e); }
    setComparing(false);
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="animate-spin text-zinc-500" /></div>;
  if (!data) return null;

  const { patterns, overall_metrics } = data;

  return (
    <div className="space-y-6" data-testid={`analyzer-${market}`}>
      {/* Overall */}
      <Card className="bg-zinc-900/80 border-zinc-800">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm text-zinc-400">{label} — Overall ({data.total_bets} bets)</CardTitle>
        </CardHeader>
        <CardContent><MetricsRow m={overall_metrics} /></CardContent>
      </Card>

      {/* Controls */}
      <Card className="bg-zinc-900/80 border-zinc-800">
        <CardContent className="pt-4">
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2">
              <SlidersHorizontal size={14} className="text-zinc-500" />
              <span className="text-xs text-zinc-500">Min N:</span>
              <Select value={String(minSample)} onValueChange={v => setMinSample(Number(v))}>
                <SelectTrigger className="w-20 h-8 bg-zinc-800 border-zinc-700 text-xs" data-testid="min-sample-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {MIN_SAMPLE_OPTIONS.map(n => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-zinc-500">Sort:</span>
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="w-36 h-8 bg-zinc-800 border-zinc-700 text-xs" data-testid="sort-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SORT_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
              <Button variant="ghost" size="sm" className="h-8 px-2" onClick={() => setAscending(!ascending)}
                data-testid="sort-direction-btn">
                {ascending ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
              </Button>
            </div>
            <Badge variant="outline" className="text-xs text-zinc-500">
              min_sample: {data.min_sample_used}
            </Badge>
          </div>
        </CardContent>
      </Card>

      {/* Singles */}
      <PatternSection title="Single Filters" icon={Shield} patterns={patterns.singles}
        sortBy={sortBy} ascending={ascending} onCompareA={setCompareA} onCompareB={setCompareB} defaultShow={10} />

      {/* Pairs */}
      <PatternSection title="Filter Pairs" icon={TrendingUp} patterns={patterns.pairs}
        sortBy={sortBy} ascending={ascending} onCompareA={setCompareA} onCompareB={setCompareB} defaultShow={8} />

      {/* Triples */}
      <PatternSection title="Filter Triples" icon={FlaskConical} patterns={patterns.triples}
        sortBy={sortBy} ascending={ascending} onCompareA={setCompareA} onCompareB={setCompareB} defaultShow={6} />

      {/* Compare */}
      <Card className="bg-zinc-900/80 border-zinc-800">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2">
            <ArrowRightLeft size={14} /> Compare Patterns
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <span className="text-xs text-zinc-500">Pattern A:</span>
              {compareA ? <FilterTags filters={compareA} /> : <span className="text-xs text-zinc-600 ml-2">Click 'A' on any pattern</span>}
            </div>
            <div>
              <span className="text-xs text-zinc-500">Pattern B:</span>
              {compareB ? <FilterTags filters={compareB} /> : <span className="text-xs text-zinc-600 ml-2">Click 'B' on any pattern</span>}
            </div>
          </div>
          <Button size="sm" onClick={runCompare} disabled={!compareA || !compareB || comparing}
            className="bg-indigo-600 hover:bg-indigo-700" data-testid="compare-btn">
            {comparing ? <Loader2 className="animate-spin mr-1" size={14} /> : <ArrowRightLeft size={14} className="mr-1" />}
            Compare
          </Button>
          {compareResult && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
              <div className="border border-zinc-800 rounded p-2 space-y-1">
                <span className="text-xs text-zinc-400">A:</span>
                <FilterTags filters={compareResult.pattern_a.filters} />
                <MetricsRow m={compareResult.pattern_a.metrics} compact />
              </div>
              <div className="border border-zinc-800 rounded p-2 space-y-1">
                <span className="text-xs text-zinc-400">B:</span>
                <FilterTags filters={compareResult.pattern_b.filters} />
                <MetricsRow m={compareResult.pattern_b.metrics} compact />
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

/* ─── Telegram Suggestions Tab ──────────────────────────────── */
function TelegramSuggestionsTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const json = await apiGet('/tt/pattern-lab/telegram-suggestions');
        setData(json);
      } catch (e) { console.error(e); }
      setLoading(false);
    })();
  }, []);

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="animate-spin text-zinc-500" /></div>;
  if (!data) return null;

  const markets = [
    { key: 'mw', label: 'Match Winner' },
    { key: 'hc', label: 'Handicap' },
    { key: 'ou', label: 'Over/Under' },
  ];

  return (
    <div className="space-y-6" data-testid="telegram-suggestions">
      {markets.map(({ key, label }) => {
        const s = data.suggestions[key];
        if (!s || s.status === 'no_data') return null;
        return (
          <Card key={key} className="bg-zinc-900/80 border-zinc-800">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <Send size={14} /> {label}
                <Badge variant="outline" className="ml-auto text-xs">{s.total_bets} bets | ROI: {s.overall_roi}%</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {s.best_odds_range?.filter && (
                  <div className="border border-zinc-800 rounded p-2">
                    <span className="text-xs text-zinc-500">Best Odds Range</span>
                    <div className="mt-1"><FilterTags filters={[s.best_odds_range.filter]} /></div>
                    <MetricsRow m={s.best_odds_range.metrics} compact />
                  </div>
                )}
                {s.best_confidence_range?.filter && (
                  <div className="border border-zinc-800 rounded p-2">
                    <span className="text-xs text-zinc-500">Best Confidence Range</span>
                    <div className="mt-1"><FilterTags filters={[s.best_confidence_range.filter]} /></div>
                    <MetricsRow m={s.best_confidence_range.metrics} compact />
                  </div>
                )}
              </div>
              <div>
                <h4 className="text-xs font-semibold text-emerald-400 mb-2 flex items-center gap-1">
                  <TrendingUp size={12} /> Recommended Filters
                </h4>
                <div className="space-y-2">
                  {s.recommended_filters?.map((r, i) => (
                    <div key={i} className="border border-emerald-900/30 rounded p-2 space-y-1">
                      <FilterTags filters={r.filters} />
                      <MetricsRow m={r.metrics} compact />
                    </div>
                  ))}
                </div>
              </div>
              {s.filters_to_avoid?.length > 0 && (
                <div>
                  <h4 className="text-xs font-semibold text-red-400 mb-2 flex items-center gap-1">
                    <AlertTriangle size={12} /> Filters to Avoid
                  </h4>
                  <div className="space-y-2">
                    {s.filters_to_avoid.map((a, i) => (
                      <div key={i} className="border border-red-900/30 rounded p-2 space-y-1">
                        <FilterTags filters={a.filters} />
                        <MetricsRow m={a.metrics} compact />
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}

/* ─── Presets Tab ────────────────────────────────────────────── */
function PresetsTab() {
  const [presets, setPresets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState('');
  const [market, setMarket] = useState('mw');
  const [filters, setFilters] = useState('');
  const [saving, setSaving] = useState(false);

  const loadPresets = useCallback(async () => {
    try {
      const json = await apiGet('/tt/pattern-lab/presets');
      setPresets(json);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  useEffect(() => { loadPresets(); }, [loadPresets]);

  const save = async () => {
    if (!name || !filters) return;
    setSaving(true);
    try {
      await apiPost('/tt/pattern-lab/presets', {
        name, market,
        filters: filters.split(',').map(f => f.trim()),
      });
      setName(''); setFilters('');
      await loadPresets();
    } catch (e) { console.error(e); }
    setSaving(false);
  };

  const remove = async (presetName) => {
    try {
      await apiDelete(`/tt/pattern-lab/presets/${encodeURIComponent(presetName)}`);
      await loadPresets();
    } catch (e) { console.error(e); }
  };

  return (
    <div className="space-y-6" data-testid="presets-tab">
      <Card className="bg-zinc-900/80 border-zinc-800">
        <CardHeader className="pb-2"><CardTitle className="text-sm">Save New Preset</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <Input placeholder="Preset name" value={name} onChange={e => setName(e.target.value)}
              className="bg-zinc-800 border-zinc-700 text-sm" data-testid="preset-name-input" />
            <Select value={market} onValueChange={setMarket}>
              <SelectTrigger className="bg-zinc-800 border-zinc-700 text-sm" data-testid="preset-market-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="mw">MW</SelectItem>
                <SelectItem value="hc">HC</SelectItem>
                <SelectItem value="ou">O/U</SelectItem>
              </SelectContent>
            </Select>
            <Input placeholder="Filters (comma separated)" value={filters} onChange={e => setFilters(e.target.value)}
              className="bg-zinc-800 border-zinc-700 text-sm" data-testid="preset-filters-input" />
          </div>
          <Button size="sm" onClick={save} disabled={saving || !name || !filters}
            className="bg-indigo-600 hover:bg-indigo-700" data-testid="save-preset-btn">
            {saving ? <Loader2 className="animate-spin mr-1" size={14} /> : <Save size={14} className="mr-1" />}
            Save Preset
          </Button>
        </CardContent>
      </Card>
      {loading ? (
        <div className="flex justify-center py-8"><Loader2 className="animate-spin text-zinc-500" /></div>
      ) : presets.length === 0 ? (
        <p className="text-zinc-500 text-sm text-center py-8">No presets saved yet</p>
      ) : (
        <div className="space-y-2">
          {presets.map((p, i) => (
            <div key={i} className="border border-zinc-800 rounded-lg p-3 flex items-center justify-between">
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold">{p.name}</span>
                  <Badge variant="outline" className="text-xs">{p.market?.toUpperCase()}</Badge>
                </div>
                <FilterTags filters={p.filters || []} />
                <span className="text-xs text-zinc-500">{p.created_at?.slice(0, 10)}</span>
              </div>
              <Button variant="ghost" size="sm" onClick={() => remove(p.name)} data-testid={`delete-preset-${i}`}>
                <Trash2 size={14} className="text-red-400" />
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ─── Main Page ─────────────────────────────────────────────── */
export default function PatternLab() {
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadOverview = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const json = await apiGet('/tt/pattern-lab/overview');
      setOverview(json);
    } catch (e) {
      // Surface the failure instead of swallowing — used to render blank
      // forever when /overview timed out, leaving the page empty.
      console.error('[PatternLab] overview load failed:', e);
      setError(e?.message || String(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadOverview(); }, [loadOverview]);

  return (
    <div className="min-h-screen p-4 md:p-6 max-w-6xl mx-auto" data-testid="pattern-lab-page">
      <div className="mb-6">
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <FlaskConical size={24} className="text-indigo-400" />
          Pattern Lab
        </h1>
        <p className="text-sm text-zinc-500 mt-1">AI Filter Analysis for Telegram Alerts</p>
      </div>

      {loading ? (
        <div className="flex justify-center py-16" data-testid="pattern-lab-loading">
          <Loader2 className="animate-spin text-zinc-500" size={32} />
        </div>
      ) : (
        <Tabs defaultValue="overview" className="space-y-4">
          <TabsList className="bg-zinc-900 border border-zinc-800">
            <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
            <TabsTrigger value="mw" data-testid="tab-mw">MW</TabsTrigger>
            <TabsTrigger value="hc" data-testid="tab-hc">HC</TabsTrigger>
            <TabsTrigger value="ou" data-testid="tab-ou">O/U</TabsTrigger>
            <TabsTrigger value="telegram" data-testid="tab-telegram">Telegram</TabsTrigger>
            <TabsTrigger value="presets" data-testid="tab-presets">Presets</TabsTrigger>
          </TabsList>

          <TabsContent value="overview">
            {error ? (
              <div className="rounded-md border border-red-800/60 bg-red-950/30 p-4 text-sm"
                   data-testid="pattern-lab-overview-error">
                <div className="text-red-300 font-semibold mb-1">Errore caricamento Overview</div>
                <div className="text-red-400/80 break-all mb-3">{error}</div>
                <Button size="sm" variant="outline" onClick={loadOverview}
                        data-testid="pattern-lab-overview-retry">
                  Riprova
                </Button>
                <div className="text-zinc-500 text-xs mt-3">
                  Le altre tab (MW, HC, O/U, Telegram, Presets) sono indipendenti
                  e funzionano comunque — selezionale dal menu sopra.
                </div>
              </div>
            ) : overview ? (
              <OverviewTab data={overview} />
            ) : (
              <div className="text-zinc-500 text-sm py-8 text-center">Nessun dato disponibile.</div>
            )}
          </TabsContent>
          <TabsContent value="mw">
            <MarketAnalyzerTab market="mw" label="Match Winner" />
          </TabsContent>
          <TabsContent value="hc">
            <MarketAnalyzerTab market="hc" label="Handicap" />
          </TabsContent>
          <TabsContent value="ou">
            <MarketAnalyzerTab market="ou" label="Over/Under" />
          </TabsContent>
          <TabsContent value="telegram">
            <TelegramSuggestionsTab />
          </TabsContent>
          <TabsContent value="presets">
            <PresetsTab />
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
}
